<template>
  <slide-out
    style="z-index: 200;"
    dock="right"
    :size="sideOutCurrentSize"
    ref="companySlideout"
    @resize="OnResize"
    :min-size="sideOutMinSize"
    :allow-resize="nested ? false : true"
    :append-to="nested ? '' : '#app'"
    :visible.sync="slideouts.active"
    v-on:close="onSlideoutClosing">
    <template v-slot:header>
      <h3 v-if="isFetching" class="font-weight-bold pa-1" small>
        <v-progress-circular :size="16" width="2" indeterminate class="mr-1"></v-progress-circular>
        Fetching Company ...
      </h3>
      <h3 v-else-if="selected.id" class="font-weight-bold pa-1" small>
        <v-icon left>mdi-pencil-box-outline</v-icon> Update: '{{ selected.name }}'
      </h3>
      <h3 v-else class="font-weight-bold pa-1" small>
        <v-icon left>mdi-plus-box-outline</v-icon> Add Company
      </h3>
      <v-btn @click="closeUpdateSlideout()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </template>

    <div class="h-100">
      <div class="text-center d-flex flex-column" v-if="isFetching">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading company data...</p>
      </div>
      <v-tabs
        class="editCompanyTabs h-100"
        v-else
        v-model="slideouts.tab"
        :fixedTabs="!!selected && !!selected.id">
        <v-tab>
          <v-icon small left>mdi-information-outline</v-icon>
          Basic information</v-tab
        >
        <v-tab v-if="selected.id">
          <v-icon small left>mdi-cog-outline</v-icon>
          Configurations
          <!-- <v-chip x-small class="ml-1">{{ selected.usersCount }}</v-chip> -->
        </v-tab>
        <v-tab v-if="!selected.id">
          <v-icon small left>mdi-shield-key-outline</v-icon>
          Permissions
          <v-icon v-if="!selected.id" small color="error" class="ml-1">mdi-asterisk</v-icon>
        </v-tab>
        <v-tab v-if="selected.id">
          <v-icon small left>mdi-folder-multiple-outline</v-icon>
          Facilities
          <v-chip x-small class="ml-1">{{ selected.facilitiesLimitUsed }}</v-chip>
        </v-tab>
        <v-tab v-if="selected.id">
          <v-icon small left>mdi-account-multiple-outline</v-icon>
          Users
          <v-chip x-small class="ml-1">{{ selected.usersCount }}</v-chip>
        </v-tab>
        <v-tab v-if="selected.id">
          <v-icon small left>mdi-account-multiple-outline</v-icon>
          Permissions
          <!-- <v-chip x-small class="ml-1">{{ selected.rolesCount }}</v-chip> -->
        </v-tab>
        <v-tab-item class="h-100 overflow-visible">
          <CompanyBasicInformation
            ref="editCompanyForm"
            v-model="valid"
            :selected="selected"
            @changed="onDraftChanged"
          />
        </v-tab-item>
        <v-tab-item v-if="selected.id">
          <CompanySettings :companyId="selected.id" />
        </v-tab-item>
        <v-tab-item v-if="!selected.id">
          <v-alert
            v-if="!selected.id"
            dense
            type="warning"
            border="left"
            class="mt-2 mx-4"
          >
            <strong>Required:</strong> Please select at least one permission for the new company.
          </v-alert>
          <permissions-tree
            ref="permissionsTree"
            :companyId="selected.id || 'new'"
            @selection-change="onPermissionsChange"
          />
        </v-tab-item>
        <v-tab-item v-if="selected.id">
          <view-only-facility-list
            :loading="GET_FACILITIES_LOADING"
            :company="selected"></view-only-facility-list>
        </v-tab-item>
        <v-tab-item v-if="selected.id">
          <view-only-user-list :company="selected" :entries="selected.users"></view-only-user-list>
        </v-tab-item>
        <v-tab-item v-if="selected.id">
          <management-role-list v-if="selected" :companyId="selected.id"></management-role-list>
        </v-tab-item>
      </v-tabs>
    </div>
    <template v-slot:footer>
      <v-card-actions>
        <v-btn text color="error" @click="closeUpdateSlideout()">
          <v-icon left>mdi-chevron-left</v-icon> Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!isFormValid || slideouts.isLoading || !slideouts.hasChanges"
          @click="updateConfirmed()"
          :loading="slideouts.isLoading">
          <v-icon left>{{ selected.id ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          {{ selected.id ? 'Save Changes' : 'Create' }}
        </v-btn>
        <v-tooltip v-if="!selected.id && !hasPermissionsSelected && valid" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="error"
              class="ml-2"
              v-bind="attrs"
              v-on="on"
            >mdi-alert-circle</v-icon>
          </template>
          <span>Permissions are required when creating a new company</span>
        </v-tooltip>
      </v-card-actions>
    </template>
  </slide-out>
</template>

<script>
  import formRules from '@Share/config/form-rules';
  import ViewOnlyUserList from '@User/components/ViewOnlyUserList.vue';
  import ViewOnlyFacilityList from '@Facility/components/ViewOnlyFacilityList.vue';
  import { $handleError, $isEqual, $formatDate } from '@Utils';
  // import DatePicker from '@Share/components/VDatePicker';
  // import SingleImageUploader from '@Share/components/SingleImageUploader.vue';
  import PermissionsTree from '@Role/components/PermissionsTree.vue';

  import { mapGetters } from 'vuex';
  import CompanySettings from './CompanySettings.vue';
  import { LogoVariant } from '@Share/config/enums';
  import ManagementRoleList from '@Role/views/ManagementRoleList.vue';
  import CompanyBasicInformation from './CompanyBasicInformation.vue';
  export default {
    name: 'edit-company',
    data() {
      return {
        valid: false,
        formRules,
        LogoVariant,
        isFetching: false,
        selected: { 
          owner: {},
          customUI: {
            loginBackgroundImageUrl: '',
            techByLogoType: LogoVariant.WHITE
          },
          logoUrl: '',
        },
        selectedPermissions: [],
        modal: false,
        selectedCemented: { owner: {} },
      
        slideouts: {
          tab: 0,
          active: false,
          isLoading: false,
          isClosing: false,
          hasChanges: false,
        },
      };
    },
    props: {
      nested: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters('COMPANY', ['GET_FACILITIES_LOADING']),
      regions(){
        return this.$store.getters['COMPANY/GET_REGIONS'];
      },
      hasPermissionsSelected() {
        return this.selectedPermissions && this.selectedPermissions.length > 0;
      },
      isFormValid() {
        // For new companies, require permissions to be selected
        if (!this.selected.id) {
          return this.valid && this.hasPermissionsSelected;
        }
        return this.valid;
      },
      sideOutMinSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_MINSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_MINSIZE', val);
        },
      },
      sideOutCurrentSize: {
        get() {
          return this.$store.getters['GET_SIDOUT_CURRENTSIZE'];
        },
        set(val) {
          this.$store.commit('SET_SIDOUT_CURRENTSIZE', val);
        },
      },
    },
    methods: {
      OnResize() {
        let companySlideout = this.$refs.companySlideout;
        this.sideOutCurrentSize = companySlideout.resizeValue;
      },
      onSlideoutClosing() {
        this.$emit('close');
        this.closeUpdateSlideout();
      },
      updateConfirmed() {
        this.slideouts.isLoading = true;
        let toSend = this.$refs.editCompanyForm && this.$refs.editCompanyForm.getDraft
          ? this.$refs.editCompanyForm.getDraft()
          : structuredClone(this.selected);
        
        // Add permissions as comma-separated string when creating a new company
        if (!toSend.id && this.selectedPermissions && this.selectedPermissions.length > 0) {
          toSend.permissions = this.selectedPermissions.join(',');
        }
        
        this.$store
          .dispatch('COMPANY/UPDATE', toSend)
          .then(() => {
            this.closeUpdateSlideout();
          })

          .finally(() => {
            this.slideouts.isLoading = false;
          });
      },
      closeUpdateSlideout() {
        this.selected = { 
          owner: {},
          customUI: {
            loginBackgroundImageUrl: '',
            techByLogoType: LogoVariant.WHITE
          },
          logoUrl: '',
        };
        this.selectedPermissions = [];
        this.isFetching = false;
        this.cementCompany();
        this.slideouts.active = false;
        this.valid = true;
        if(this.$refs.editCompanyForm) {
          this.$refs.editCompanyForm.resetValidation();
        }
      },
      open(id) {
        if (id != null) {
          this.companyId = id;
          this.fetchCompany();
          setTimeout(() => {
            this.slideouts.tab = 0;
          }, 250);
        }
        this.slideouts.active = true;
      },
      $formatDate,
      fetchCompany() {
        this.isFetching = true;
        this.$store
          .dispatch('COMPANY/GET_BY_ID', this.companyId)
          .then((resp) => {
            this.isFetching = false;
            this.slideouts.isLoading = false;
            const data = structuredClone(resp.data);
            data.licenseExpiryDate = $formatDate(data.licenseExpiryDate, 'YYYY-MM-DD');
            
            // Handle logo type from API
            if (data.customUI.techByLogoType) {
              // If it's already a number, verify it's valid
              if (typeof data.customUI.techByLogoType === 'number') {
                if (![this.LogoVariant.WHITE, this.LogoVariant.BLACK].includes(data.customUI.techByLogoType)) {
                  data.customUI.techByLogoType = this.LogoVariant.WHITE; // Fallback to default if invalid
                }
              } else {
                // Default if not a number
                data.customUI.techByLogoType = this.LogoVariant.WHITE;
              }
            } else {
              // Default if not set
              data.customUI.techByLogoType = this.LogoVariant.WHITE;
            }
            
            this.selected = {
              ...this.selected,
              ...data
            };
            this.cementCompany();
          })
          .catch((err) => {
            this.isFetching = false;
            this.closeUpdateSlideout();
            $handleError(err);
          });
      },
      cementCompany() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.slideouts.active) return;
        const current = this.$refs.editCompanyForm && this.$refs.editCompanyForm.getDraft
          ? this.$refs.editCompanyForm.getDraft()
          : this.selected;
        this.slideouts.hasChanges = !$isEqual(current, this.selectedCemented);
      },
      onDraftChanged(draft) {
        if (!this.slideouts.active) return;
        this.slideouts.hasChanges = !$isEqual(draft, this.selectedCemented);
      },
      selectLogoType(type) {
        this.selected.customUI.techByLogoType = type;
      },
      onPermissionsChange(permissions) {
        this.selectedPermissions = permissions;
        this.slideouts.hasChanges = true;
      },
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
      'slideouts.tab'(val) {
        if (!this.selected.facilities && val == 1&&this.selected.id) {
          this.$store.dispatch('COMPANY/FETCH_FACILITIES', this.companyId).then((res) => {
            this.selected.facilities = res.data.items;
            this.selected.projectsCount = res.data.total;
          });
        }
      },
      nested: {
        handler() {
          this.nested ? (this.sideOutCurrentSize = '80vw') : (this.sideOutCurrentSize = '35vw');
          this.nested ? (this.sideOutMinSize = '80vw') : (this.sideOutMinSize = '35vw');
        },
        deep: true,
      },
    },
    components: {
      ViewOnlyUserList,
      ViewOnlyFacilityList,
      // DatePicker,
      // SingleImageUploader,
      CompanySettings,
      ManagementRoleList,
      CompanyBasicInformation,
      PermissionsTree
    },
  };
</script>

<style>
  .editCompanyTabs .v-window {
    overflow: visible;
  }

  .logo-selection-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .logo-option {
    position: relative;
    width: 180px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px;
    cursor: pointer;
    user-select: none;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .logo-option:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  .logo-option-selected {
    border: 2px solid var(--v-success-base);
    background-color: rgba(76, 175, 80, 0.05);
  }

  .logo-preview {
    width: 100%;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }

  .white-logo {
    background-color: #c3c3c3;
    border: 1px solid #e0e0e0;
  }

  .black-logo {
    background-color: white;
    border: 1px solid #e0e0e0;
  }

  .logo-label {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7);
    margin-bottom: 4px;
  }

  .logo-check-icon {
    position: absolute;
    top: 2px;
    right: 2px;
    font-size: 24px;
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
  }

  /* Dark theme support */
  .v-application.theme--dark .logo-label {
    color: rgba(255, 255, 255, 0.8);
  }

  .v-application.theme--dark .black-logo {
    background-color: #333;
    border-color: #444;
  }

  .v-application.theme--dark .white-logo {
    background-color: #424242;
    border-color: #555;
  }

  .v-application.theme--dark .logo-option {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .v-application.theme--dark .logo-option-selected {
    background-color: rgba(76, 175, 80, 0.1);
  }

  @media (max-width: 600px) {
    .logo-selection-wrapper {
      justify-content: center;
    }
    
    .logo-option {
      width: 160px;
      padding: 12px;
    }
  }

  /* SingleImageUploader handles its own styling */
</style>
